// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useState } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { useAppSelector } from '@advisor-app/store/store';
import { type StockFilters } from '@shared/data-access/interfaces/api/ai/screener';
import { useTranslation } from 'react-i18next';
import { citecApi } from '@advisor-app/api/engine/citec-api';
import { type Universe } from '@shared/data-access/interfaces/api/engine/citec';
import { showModal } from '@shared/ui-kit/components/popups/show-modal';
import { useHandleApiErrors } from '@advisor-app/hooks/use-handle-api-errors';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { selectUniverseFilters } from '@advisor-app/store/slices/parameters';
import { screenerApi } from '@advisor-app/store/api/ai/screener-api';
import dayjs from 'dayjs';

export const useHandlerEquityScreener = (id: string) => {
  const { t } = useTranslation();
  const handleApiErrors = useHandleApiErrors();
  const runSafeUpdate = useSafeUpdate();
  const universe = useAppSelector(state => selectUniverseFilters(state, id))!;

  const [isDownloadingUniverse, setIsDownloadingUniverse] = useState(false);

  const { factors_filter, universe_base, esg_scoring, ...rest } = universe;
  const [searchScreener] = screenerApi.useLazySearchScreenerQuery();

  const generateFactorsPayload = () => {
    const lowerFactors: number[] = [];
    const upperFactors: number[] = [];
    const factor: string[] = [];

    for (let i = 0; i < factors_filter.length; i++) {
      const element = factors_filter[i];
      lowerFactors.push(element.ranges[0] / 100);
      upperFactors.push(element.ranges[1] / 100);
      factor.push(element.factor);
    }

    const esg_factors_keys: string[] = [];
    const esg_lower: number[] = [];
    const esg_upper: number[] = [];

    esg_scoring.forEach(factor => {
      esg_factors_keys.push(factor.factor);
      if (factor.factor === 'co2_emissions') {
        esg_lower.push(factor.ranges[0] * 5000000);
        esg_upper.push(factor.ranges[1] * 5000000);
      } else {
        esg_lower.push(factor.ranges[0]);
        esg_upper.push(factor.ranges[1]);
      }
    });

    return [
      factor,
      lowerFactors,
      upperFactors,
      esg_factors_keys,
      esg_lower,
      esg_upper,
    ] as const;
  };

  const handleExportUniverseScreener = async () => {
    try {
      runSafeUpdate(() => setIsDownloadingUniverse(true));

      const [factor, lower, upper, esg_factors_keys, esg_lower, esg_upper] =
        generateFactorsPayload();

      const payload = {
        universe_base: {
          name: universe_base,
          description: universe_base,
        },
        factors_filter: {
          factor,
          lower,
          upper,
        },
        esg_scoring: {
          filter: esg_factors_keys,
          lower: esg_lower,
          upper: esg_upper,
        },
        ...rest,
      } as StockFilters;

      const data = await searchScreener({ payload, assetType: 'stock' }).unwrap();

      const wordBook = XLSX.utils.book_new();
      const wordSheet = XLSX.utils.json_to_sheet(data as never);

      XLSX.utils.book_append_sheet(wordBook, wordSheet);
      XLSX.writeFile(wordBook, `${universe_base}.xlsx`);
      runSafeUpdate(() => setIsDownloadingUniverse(false));
    } catch (error: any) {
      handleApiErrors(error);
    }
  };

  const handleSaveScreener = (onSaveScreener?: VoidFunction) => async () => {
    showModal({
      title: t('research.save-screener', { ns: 'research' }),
      content: t('research.save-as', { ns: 'research' }),
      input: { type: 'text', placeholder: t('common.name', { ns: 'common' }) },
      buttons: {
        confirm: {
          show: true,
          text: t('common.save', { ns: 'common' }),
        },
        cancel: {
          show: true,
          text: t('common.cancel', { ns: 'common' }),
        },
      },
      preConfirm: name => {
        const [factor, lower, upper, esg_factors_keys, esg_lower, esg_upper] =
          generateFactorsPayload();

        const payload = {
          description: name,
          creation_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          universe_base: {
            name: universe_base,
            description: universe_base,
          },
          factors_filter: {
            factor,
            lower,
            upper,
          },
          esg_scoring: {
            filter: esg_factors_keys,
            lower: esg_lower,
            upper: esg_upper,
          },
          ...(rest as any),
        } as Universe;

        return citecApi.postUniverse(payload);
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(result => {
      if (result.isConfirmed) {
        onSaveScreener?.();
      }
    });
  };

  return {
    isDownloadingUniverse,
    handleExportUniverseScreener,
    handleSaveScreener,
  };
};
